import { useState } from 'react';
import { toast } from 'sonner';
import { LayoutGrid, Music, Tag, Timer, Plus, Trash2, Download } from 'lucide-react';
import api from '../../lib/api';

const Section = ({ icon: Icon, title, checked, onToggle, children, testId }) => (
  <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3" data-testid={testId}>
    <label className="flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={onToggle} className="w-3.5 h-3.5 accent-violet-500" data-testid={`${testId}-toggle`} />
      <Icon size={13} className="text-violet-400" />
      <span className="text-xs text-zinc-100 font-medium">{title}</span>
    </label>
    {checked && <div className="mt-2.5 pl-6 space-y-2">{children}</div>}
  </div>
);

export const DynamicBlocksPanel = ({ content, setContent, events = [], selectedEvent, eventUrlBase }) => {
  const [showEvents, setShowEvents] = useState(!!(content.events || []).length);
  const [showLineup, setShowLineup] = useState(!!(content.lineup || []).length);
  const [showDiscount, setShowDiscount] = useState(!!content.discount_code);
  const [showCountdown, setShowCountdown] = useState(!!content.countdown_date);
  const [importingLineup, setImportingLineup] = useState(false);

  const urlBase = eventUrlBase || `${window.location.origin}/evento`;
  const pickedIds = (content.events || []).map(e => e._id);

  const toggleEvent = (ev) => {
    setContent(c => {
      const list = c.events || [];
      if (list.find(e => e._id === ev.id)) return { ...c, events: list.filter(e => e._id !== ev.id) };
      if (list.length >= 4) { toast.error('Máximo 4 eventos en el grid'); return c; }
      return {
        ...c,
        events: [...list, {
          _id: ev.id,
          title: ev.title,
          date_label: ev.date ? new Date(ev.date).toLocaleDateString('es-AR', { day: '2-digit', month: 'short' }) : '',
          image: ev.image_url || ev.flyer_url || '',
          url: `${urlBase}/${ev.slug || ev.id}`,
        }],
      };
    });
  };

  const importLineup = async () => {
    if (!selectedEvent) return;
    setImportingLineup(true);
    try {
      const r = await api.get(`/events/${selectedEvent.id}/lineup/public`);
      const artists = (r.data || []).map(a => ({
        name: a.name || a.artist_name || '',
        photo: a.photo_url || a.image_url || '',
        role: a.genre || a.role || '',
      })).filter(a => a.name);
      if (!artists.length) return toast.info('El evento no tiene lineup cargado');
      setContent(c => ({ ...c, lineup: artists.slice(0, 8) }));
      toast.success(`${artists.length} artista(s) importado(s)`);
    } catch {
      toast.error('No se pudo importar el lineup');
    } finally {
      setImportingLineup(false);
    }
  };

  const updateArtist = (idx, key, val) => setContent(c => {
    const list = [...(c.lineup || [])];
    list[idx] = { ...list[idx], [key]: val };
    return { ...c, lineup: list };
  });

  return (
    <div className="space-y-2" data-testid="dynamic-blocks-panel">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Bloques dinámicos</div>

      {/* Multi-event grid */}
      <Section icon={LayoutGrid} title="Grid de eventos (newsletter multi-evento)" checked={showEvents} testId="block-events"
        onToggle={(e) => { setShowEvents(e.target.checked); if (!e.target.checked) setContent(c => ({ ...c, events: undefined })); }}>
        {events.length === 0 ? (
          <p className="text-[11px] text-zinc-500 italic">No hay eventos publicados para agregar</p>
        ) : (
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {events.map(ev => (
              <label key={ev.id} className="flex items-center gap-2 cursor-pointer text-[11px] text-zinc-300 hover:text-white">
                <input
                  type="checkbox"
                  checked={pickedIds.includes(ev.id)}
                  onChange={() => toggleEvent(ev)}
                  className="w-3 h-3 accent-violet-500"
                  data-testid={`grid-event-${ev.id}`}
                />
                <span className="truncate">{ev.title}</span>
                {ev.date && <span className="text-zinc-500 flex-shrink-0">{new Date(ev.date).toLocaleDateString('es-AR')}</span>}
              </label>
            ))}
          </div>
        )}
        <p className="text-[10px] text-zinc-500">Máximo 4 eventos — se muestran en grilla de 2 columnas.</p>
      </Section>

      {/* Lineup */}
      <Section icon={Music} title="Line up / DJs con fotos" checked={showLineup} testId="block-lineup"
        onToggle={(e) => { setShowLineup(e.target.checked); if (!e.target.checked) setContent(c => ({ ...c, lineup: undefined })); }}>
        {selectedEvent && (
          <button
            type="button"
            onClick={importLineup}
            disabled={importingLineup}
            className="px-2.5 py-1.5 bg-violet-600/20 border border-violet-500/40 text-violet-200 rounded-md text-[11px] flex items-center gap-1.5 hover:bg-violet-600/30"
            data-testid="import-lineup-btn"
          >
            <Download size={11} /> Importar lineup de &quot;{selectedEvent.title}&quot;
          </button>
        )}
        {(content.lineup || []).map((a, idx) => (
          <div key={idx} className="flex items-center gap-1.5">
            {a.photo && <img src={a.photo} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" />}
            <input value={a.name} onChange={(e) => updateArtist(idx, 'name', e.target.value)} placeholder="Nombre"
              className="flex-1 min-w-0 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-white text-[11px]" data-testid={`lineup-name-${idx}`} />
            <input value={a.role || ''} onChange={(e) => updateArtist(idx, 'role', e.target.value)} placeholder="Género/rol"
              className="w-20 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-white text-[11px]" />
            <input value={a.photo || ''} onChange={(e) => updateArtist(idx, 'photo', e.target.value)} placeholder="URL foto"
              className="w-20 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-white text-[11px]" />
            <button type="button" onClick={() => setContent(c => ({ ...c, lineup: c.lineup.filter((_, i) => i !== idx) }))}
              className="text-zinc-500 hover:text-red-400" data-testid={`lineup-remove-${idx}`}>
              <Trash2 size={12} />
            </button>
          </div>
        ))}
        {(content.lineup || []).length < 8 && (
          <button type="button" onClick={() => setContent(c => ({ ...c, lineup: [...(c.lineup || []), { name: '', photo: '', role: '' }] }))}
            className="text-[11px] text-violet-300 hover:text-violet-200 flex items-center gap-1" data-testid="lineup-add-btn">
            <Plus size={11} /> Agregar artista
          </button>
        )}
      </Section>

      {/* Discount code highlight */}
      <Section icon={Tag} title="Código de descuento destacado" checked={showDiscount} testId="block-discount"
        onToggle={(e) => { setShowDiscount(e.target.checked); if (!e.target.checked) setContent(c => ({ ...c, discount_code: undefined, discount_note: undefined })); }}>
        <input
          type="text"
          value={content.discount_code || ''}
          onChange={(e) => setContent(c => ({ ...c, discount_code: e.target.value.toUpperCase() }))}
          placeholder="Ej: VERANO25"
          className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-2.5 py-1.5 text-white text-xs font-mono tracking-widest"
          data-testid="discount-code-input"
        />
        <input
          type="text"
          value={content.discount_note || ''}
          onChange={(e) => setContent(c => ({ ...c, discount_note: e.target.value }))}
          placeholder="Ej: 25% OFF · válido hasta el domingo"
          className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-2.5 py-1.5 text-white text-xs"
          data-testid="discount-note-input"
        />
      </Section>

      {/* Countdown */}
      <Section icon={Timer} title="Countdown visual (faltan X días)" checked={showCountdown} testId="block-countdown"
        onToggle={(e) => {
          const on = e.target.checked;
          setShowCountdown(on);
          if (!on) return setContent(c => ({ ...c, countdown_date: undefined, countdown_label: undefined }));
          if (selectedEvent?.date) setContent(c => ({ ...c, countdown_date: selectedEvent.date }));
        }}>
        <input
          type="datetime-local"
          value={content.countdown_date ? new Date(content.countdown_date).toISOString().slice(0, 16) : ''}
          onChange={(e) => setContent(c => ({ ...c, countdown_date: e.target.value ? new Date(e.target.value).toISOString() : undefined }))}
          className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-2.5 py-1.5 text-white text-xs"
          data-testid="countdown-date-input"
        />
        <input
          type="text"
          value={content.countdown_label || ''}
          onChange={(e) => setContent(c => ({ ...c, countdown_label: e.target.value }))}
          placeholder="Ej: Falta cada vez menos"
          className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-2.5 py-1.5 text-white text-xs"
          data-testid="countdown-label-input"
        />
        <p className="text-[10px] text-zinc-500">El número se calcula al momento del envío (estático en el mail).</p>
      </Section>
    </div>
  );
};
